import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { HOLIDAY_TYPES, BLOG_POSTS, HOW_IT_WORKS, PACKAGES, HOME_VIDEOS } from '../mock';
import { PageHero } from './DestinationsPages';
import { PackageCard } from '../components/sections/Packages';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner } from '../components/sections/CTASections';
import { Calendar, User, ArrowRight, Search, Phone, MessageCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import VideoGallery from '../components/sections/VideoGallery';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import { getVideoReviews } from '../constants/videoReviews';
import { Input } from '../components/ui/input';
import { COMPANY } from '../mock';

export function HowItWorksPage() {
  return (
    <div>
      <Helmet>
        <title>How Booking Works | Quote - Customize - Book | Mountains Chain Travel</title>
        <meta name="description" content="Quote - Customize - Book. Easy Process In Minutes. Learn how Mountains Chain Travel crafts your perfect journey in 4 simple steps." />
        <meta property="og:title" content="How Booking Works | Quote - Customize - Book | Mountains Chain Travel" />
        <meta property="og:description" content="Quote - Customize - Book. Easy Process In Minutes. Learn how Mountains Chain Travel crafts your perfect journey in 4 simple steps." />
      </Helmet>
      <PageHero title="How It Works" sub="From your first message to your unforgettable trip - in 4 simple steps" image="https://images.unsplash.com/photo-1607836046730-3317bd58a31b?crop=entropy&cs=srgb&fm=jpg&q=85"/>
      <CustomerVideoReviews />
      <section id="how-booking-works" className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOW_IT_WORKS.map((s, i) => (
            <div key={i} className="relative bg-white border border-brand-greyDark rounded-2xl p-6 card-hover animate-fade-up" style={{animationDelay: `${i*100}ms`}}>
              <div className="absolute -top-4 left-6 w-10 h-10 rounded-xl bg-brand-orange text-white font-poppins font-bold flex items-center justify-center shadow-glow-orange">{i+1}</div>
              <div className="text-[28px] mt-3" aria-hidden>{s.emoji}</div>
              <h3 className="font-poppins font-bold text-brand-blue text-[18px] mt-2">{s.title}</h3>
              <p className="text-brand-blue/65 text-[14px] mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 bg-brand-grey rounded-3xl p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-tight">Built around <span className="text-brand-orange">you</span>, not a brochure.</h2>
            <p className="text-brand-blue/70 mt-4 text-[15px] leading-relaxed">Most agencies sell you a fixed package. We design a trip around your travel style, budget, pace and dreams. From the first WhatsApp message to your last photograph at home - we are with you.</p>
            <ul className="mt-6 space-y-2.5">
              {['Dedicated travel designer (real human)', 'Free changes & revisions before booking', '20% to book - rest before departure', '24x7 on-trip support on WhatsApp & call', 'Verified hotels & licensed drivers only'].map((x,i) => (
                <li key={i} className="flex items-start gap-2.5 text-brand-blue/80 text-[14.5px]"><CheckCircle2 size={18} className="text-brand-orange mt-0.5 shrink-0"/>{x}</li>
              ))}
            </ul>
            <div className="flex gap-3 mt-7">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d love to start planning my trip with you.\n\nPlease share how we can get started.\n\nThank you!')}`} target="_blank" rel="noreferrer"><Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-11 px-5 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>Start on WhatsApp</Button></a>
              <a href={`tel:${COMPANY.phone}`}><Button variant="outline" className="border-brand-blue/20 text-brand-blue h-11 px-5 rounded-xl"><Phone size={16} className="mr-2"/>Call Us</Button></a>
            </div>
          </div>
          <img src="https://images.unsplash.com/photo-1595815771614-ade9d652a65d?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Plan trip" className="rounded-2xl object-cover h-80 w-full shadow-card"/>
        </div>
      </section>
      <CTABanner/>
      <EnquiryForm/>
    </div>
  );
}

export function HolidayCategoryPage() {
  const { slug } = useParams();
  const hol = HOLIDAY_TYPES.find(h => h.slug === slug);
  if (!hol) return <div className="max-w-7xl mx-auto px-6 py-20 text-center">Holiday type not found.</div>;

  if (slug === 'himachal-taxi-tours' || slug === 'ladakh-taxi-tours') {
    const isHimachal = slug === 'himachal-taxi-tours';
    const regionSlug = isHimachal ? 'himachal-pradesh' : 'leh-ladakh';
    const regionName = isHimachal ? 'Himachal' : 'Ladakh';
    const regionRoute = isHimachal ? 'Himachal' : 'Leh Ladakh';
    const regionTagline = isHimachal
      ? 'From Shimla\'s colonial charm to Spiti\'s moonland - explore every corner of Himachal in your own private cab.'
      : 'Conquer the world\'s highest passes, explore ancient monasteries, and camp by Pangong - all from the comfort of a private SUV.';
    const heroImg = isHimachal
      ? '/images/Dashboard/Assets/Images/Destination_Images/Himachal.webp'
      : '/images/Dashboard/Assets/Images/Upcoming_packages/Lehcity.jpg';
    const matching = PACKAGES.filter(p => p.tags.includes('Taxi Tour') && p.destination === regionSlug);
    const features = [
      { icon: '🚗', title: 'Private & Exclusive', desc: 'Your own private cab - no sharing, no waiting. Just you, your loved ones, and the open road.' },
      { icon: '🛣️', title: 'Stop Anywhere, Anytime', desc: 'Spot a scenic viewpoint, a hidden café, or a blooming orchard? Just ask the driver. Your itinerary, your pace.' },
      { icon: '👨‍✈️', title: 'Expert Local Driver', desc: 'Every driver knows the mountains like the back of their hand - shortcuts, best photo spots, and safe driving on every terrain.' },
      { icon: '📅', title: 'Fully Flexible Timing', desc: 'No rigid schedules. Start late, extend stops, add detours - the car is yours for the entire trip.' },
    ];
    return (
      <div>
        <Helmet>
          <title>{!isHimachal ? 'Popular Ladakh Tours - Bike Taxi Family Trips Starting From ₹13,000' : `${regionRoute} Taxi Tours - Private Cab Packages | Mountains Chain Travel`}</title>
          <meta name="description" content={!isHimachal ? 'Bike Taxi Family Trips. Starting From ₹13,000. Explore Leh Ladakh with your own private taxi.' : `Explore ${regionRoute} with your own private taxi. Flexible itineraries, expert drivers, and competitive prices. Book your ${regionName} taxi tour today!`} />
          <meta property="og:title" content={!isHimachal ? 'Popular Ladakh Tours - Bike Taxi Family Trips Starting From ₹13,000' : `${regionRoute} Taxi Tours - Private Cab Packages | Mountains Chain Travel`} />
          <meta property="og:description" content={!isHimachal ? 'Bike Taxi Family Trips. Starting From ₹13,000. Explore Leh Ladakh with your own private taxi.' : `Explore ${regionRoute} with your own private taxi. Flexible itineraries, expert drivers, and competitive prices.`} />
        </Helmet>

        {/* Hero */}
        <section id="himachal-destinations" className="relative min-h-[85vh] flex items-center overflow-hidden">
          <img src={heroImg} alt={`${regionName} - Indian Himalayas`} className="absolute inset-0 w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent z-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30 z-10" />
          <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40 animate-fade-up">
                <span className="tracking-wide">Private Cabs · Expert Drivers · Flexible Routes</span>
              </div>
              <div className="text-white/90 text-[14px] lg:text-[15px] font-semibold mb-3 animate-fade-up" style={{animationDelay: '50ms'}}>
                {isHimachal ? 'Best Taxi Service in Himachal Pradesh' : 'Best Taxi Service in Leh Ladakh'}
              </div>
              <h1 className="font-poppins text-[48px] sm:text-[56px] lg:text-[72px] text-white leading-[1.05] font-extrabold drop-shadow-2xl animate-fade-up" style={{animationDelay: '100ms'}}>
                <span className="text-brand-orange">{regionName}</span>
                <br />
                <span>Taxi Tours</span>
              </h1>
              <p className="text-white text-[18px] lg:text-[21px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg animate-fade-up" style={{animationDelay: '200ms'}}>
                Private taxi tours across {regionRoute}. Your own car, your schedule, and a local driver who knows every hidden gem.
              </p>
              {isHimachal && (
                <div className="mt-5 animate-fade-up" style={{animationDelay: '250ms'}}>
                  <div className="inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                    <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Visit</span>
                    <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                      Shimla · Kullu · Manali · Kasol · Thirthan Valley · Dharamshala · Dalhousie's · Amritsar · Kinnaur & Spiti Valley
                    </span>
                  </div>
                </div>
              )}
              {!isHimachal && (
                <div className="mt-5 animate-fade-up" style={{animationDelay: '250ms'}}>
                  <div className="inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                    <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Visit</span>
                    <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                      Zanskar Valley · Leh · Nubra Valley · Pangong · Tso · Kargil
                    </span>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-4 mt-10 animate-fade-up" style={{animationDelay: '300ms'}}>
                <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m planning a ' + regionName + ' taxi tour and would love to explore the region with a private cab.\n\nCould you please share:\n- Available taxi tour packages\n- Vehicle options & pricing\n- Customizable itineraries\n\nLooking forward to hearing from you!')}`} target="_blank" rel="noreferrer" className="group">
                  <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-13 px-8 rounded-xl text-[16px] font-bold btn-glow shadow-2xl shadow-brand-orange/40 transition-all duration-300 group-hover:scale-105">
                    <MessageCircle size={20} className="mr-2.5"/>
                    Book on WhatsApp
                  </Button>
                </a>
                <a href={`tel:${COMPANY.phone}`} className="group">
                  <Button variant="outline" className="border-2 border-white/50 text-white hover:bg-white/20 h-13 px-8 rounded-xl text-[16px] font-semibold transition-all duration-300 group-hover:scale-105">
                    <Phone size={20} className="mr-2.5"/>
                    Call Us
                  </Button>
                </a>
              </div>
              <div className="flex flex-wrap gap-8 mt-12 animate-fade-up" style={{animationDelay: '400ms'}}>
                {[
                  { num: '3L+', label: 'Happy Travelers' },
                  { num: '4.9', label: '★ Rating' },
                  { num: '10+', label: 'Years Experience' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-brand-orange font-poppins font-extrabold text-[28px] lg:text-[32px] leading-none">{stat.num}</div>
                    <div className="text-white/80 text-[12px] font-medium mt-1 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Review Cards - Right after hero */}
        <ReviewCards destinationKey={isHimachal ? 'himachal-taxi-tours' : 'leh-ladakh'} />
        <CustomerVideoReviews reviews={getVideoReviews(isHimachal ? 'himachal-taxi-tours' : 'leh-ladakh')} />

        {/* Video Reviews - Travel Diaries */}
        <VideoGallery
          videos={HOME_VIDEOS}
          title="Travel Diaries"
          sub="Your Journey Visualized"
        />

        {/* Why Choose */}
        <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-center mb-14">
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">Why a Taxi Tour?</div>
            <h2 className="font-poppins text-[34px] lg:text-[42px] text-brand-blue leading-[1.15]">Freedom on the <span className="text-brand-orange">Open Road</span></h2>
            <p className="text-brand-blue/65 text-[15px] max-w-2xl mx-auto mt-3 leading-relaxed">Forget cramped buses and rigid group tours. A taxi tour puts you in control - stop for that perfect photo, skip that crowded spot, linger where you fall in love.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <div key={i} className="bg-white border border-brand-greyDark rounded-2xl p-6 card-hover animate-fade-up text-center" style={{animationDelay: `${i*80}ms`}}>
                <div className="text-[40px] mb-3">{f.icon}</div>
                <h3 className="font-poppins font-bold text-brand-blue text-[17px]">{f.title}</h3>
                <p className="text-brand-blue/65 text-[13.5px] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Region-specific packages */}
        {matching.length > 0 && (
          <section id={!isHimachal ? 'popular-ladakh-tours' : undefined} className={isHimachal ? 'max-w-7xl mx-auto px-6 pb-20 lg:pb-24' : 'bg-brand-grey/50 py-20 lg:py-24'}>
            <div className={isHimachal ? '' : 'max-w-7xl mx-auto px-6'}>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                <div>
                  <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">{regionRoute}</div>
                  <h2 className="font-poppins text-[30px] lg:text-[38px] text-brand-blue leading-[1.1]"><span className="text-brand-orange">{regionName}</span> Taxi Tours</h2>
                  <p className="text-brand-blue/65 text-[14.5px] mt-2 max-w-xl">{regionTagline}</p>
                </div>
                <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m interested in ' + regionName + ' taxi tour packages.\n\nPlease share:\n- Available tour options & routes\n- Vehicle types & pricing\n- Customizable itineraries\n\nThanks!')}`} target="_blank" rel="noreferrer" className="text-brand-orange font-semibold border-b-2 border-brand-orange pb-1 hover:gap-2 transition-all flex items-center gap-1 self-start md:self-auto">
                  Ask on WhatsApp <ArrowRight size={16}/>
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {matching.map(p => <PackageCard key={p.id} pkg={p} />)}
              </div>
            </div>
          </section>
        )}

        {/* Vehicle Fleet */}
        <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-center mb-14">
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">Our Fleet</div>
            <h2 className="font-poppins text-[34px] lg:text-[42px] text-brand-blue leading-[1.15]">Choose Your <span className="text-brand-orange">Ride</span></h2>
            <p className="text-brand-blue/65 text-[15px] max-w-2xl mx-auto mt-3 leading-relaxed">From budget sedans to luxury 4×4s - pick the vehicle that suits your group size and terrain.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: 'Swift / Etios', seats: '4+1', price: '₹2,500/day', icon: '🚙' },
              { name: 'Ertiga / Marazzo', seats: '6+1', price: '₹3,500/day', icon: '🚐' },
              { name: 'Innova Crysta', seats: '7+1', price: '₹4,000/day', icon: '🚐' },
              { name: 'Scorpio / Thar', seats: '6+1', price: '₹6,000/day', icon: '🚙' },
              { name: 'Tempo Traveller', seats: '12-26', price: '₹7,000+/day', icon: '🚌' },
              { name: 'Fortuner', seats: '7+1', price: '₹10,000/day', icon: '🚙' },
            ].map((v, i) => (
              <div key={i} className="bg-white border border-brand-greyDark rounded-xl p-5 text-center card-hover animate-fade-up" style={{animationDelay: `${i*60}ms`}}>
                <div className="text-[32px] mb-2">{v.icon}</div>
                <div className="font-poppins font-bold text-brand-blue text-[14px]">{v.name}</div>
                <div className="text-[11.5px] text-brand-blue/50">{v.seats} seats</div>
                <div className="text-brand-orange font-bold text-[13px] mt-1">{v.price}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-brand-grey rounded-2xl p-6 lg:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="font-poppins font-bold text-brand-blue text-[18px]">Not sure which vehicle to pick?</div>
              <div className="text-brand-blue/60 text-[13.5px] mt-1">Tell us your group size and route - we'll recommend the perfect ride.</div>
            </div>
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI need help choosing the right vehicle for my ' + regionName + ' trip.\n\nGroup size: [please mention]\nRoute: [please mention]\n\nPlease suggest the best option.\n\nThanks!')}`} target="_blank" rel="noreferrer">
              <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-11 px-6 rounded-xl btn-glow shrink-0"><MessageCircle size={16} className="mr-2"/>Ask Us</Button>
            </a>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue to-brand-blue/95" />
          <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-poppins text-[32px] lg:text-[44px] text-white leading-[1.15]">Ready to Hit the Road?</h2>
            <p className="text-white/75 text-[16px] mt-3 max-w-xl mx-auto leading-relaxed">Book your private taxi tour today. Zero advance for Himachal routes, easy UPI payments, and 24x7 support throughout your journey.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d like to book a ' + regionName + ' taxi tour.\n\nPlease share available packages and pricing.\n\nLooking forward to your response!')}`} target="_blank" rel="noreferrer">
                <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-7 rounded-xl text-[15px] font-bold btn-glow shadow-xl shadow-brand-orange/30"><MessageCircle size={18} className="mr-2"/>Book Now</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-7 rounded-xl text-[15px]"><Phone size={18} className="mr-2"/>{COMPANY.phone}</Button>
              </a>
            </div>
          </div>
        </section>

        <EnquiryForm />
      </div>
    );
  }

  const matching = slug === 'fixed-departure'
    ? PACKAGES.filter(p => p.tags.includes('Fixed Departure'))
    : slug === 'spiritual'
    ? PACKAGES.filter(p => p.tags.includes('Spiritual'))
    : slug === 'adventure'
    ? PACKAGES.filter(p => p.tags.includes('Adventure'))
    : slug === 'honeymoon'
    ? PACKAGES.filter(p => p.tags.includes('Honeymoon'))
    : slug === 'heritage'
    ? PACKAGES.filter(p => p.tags.includes('Heritage'))
    : PACKAGES.slice(0, 6);
  return (
    <div>
      <Helmet>
        <title>{hol.name} Tour Packages | Mountains Chain Travel</title>
        <meta name="description" content={`Explore our curated ${hol.name.toLowerCase()} packages. ${hol.tagline} Fully customizable trips crafted by local experts.`} />
        <meta property="og:title" content={`${hol.name} Tour Packages | Mountains Chain Travel`} />
        <meta property="og:description" content={`Explore our curated ${hol.name.toLowerCase()} packages. ${hol.tagline} Fully customizable trips crafted by local experts.`} />
      </Helmet>
      <PageHero title={hol.name} sub={hol.tagline} image="https://images.unsplash.com/photo-1614591276564-7b3e69347a48?crop=entropy&cs=srgb&fm=jpg&q=85"/>
      <ReviewCards />
      <CustomerVideoReviews />
      <section className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-brand-blue/70 text-[15px] max-w-3xl leading-relaxed mb-10">Hand-picked {hol.name.toLowerCase()} packages crafted by our local experts. Every itinerary is fully customizable - dates, hotels, group size and budget. Tell us your dream and we will tailor it.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{matching.map(p => <PackageCard key={p.id} pkg={p}/>)}</div>
      </section>
      <EnquiryForm/>
    </div>
  );
}

export function BlogPage() {
  return (
    <div>
      <Helmet>
        <title>Travel Blog & Stories | Mountains Chain Travel</title>
        <meta name="description" content="Tips, itineraries, and inspiration from our local Himalayan experts. Read the Mountains Chain Travel blog for amazing travel stories." />
        <meta property="og:title" content="Travel Blog & Stories | Mountains Chain Travel" />
        <meta property="og:description" content="Tips, itineraries, and inspiration from our local Himalayan experts. Read the Mountains Chain Travel blog for amazing travel stories." />
      </Helmet>
      <PageHero title="Travel Stories & Guides" sub="Tips, itineraries and inspiration from our local Himalayan experts" image="https://images.unsplash.com/photo-1656928132166-749e2fb1c46d?crop=entropy&cs=srgb&fm=jpg&q=85"/>
      <CustomerVideoReviews />
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-10">
          <div className="flex gap-2 flex-wrap">{['All','Himachal','Kashmir','Ladakh','Tips','Honeymoon'].map((t,i) => (<button key={i} className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold ${i===0?'bg-brand-blue text-white':'bg-brand-grey text-brand-blue hover:bg-brand-greyDark'}`}>{t}</button>))}</div>
          <div className="relative w-full md:w-72"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-blue/40"/><Input placeholder="Search stories..." className="pl-9 h-10 rounded-lg"/></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BLOG_POSTS.map((p,i) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-brand-greyDark shadow-card card-hover animate-fade-up" style={{animationDelay:`${i*60}ms`}}>
              <div className="aspect-[16/10] img-zoom-wrap overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover img-zoom"/></div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-[11.5px] text-brand-blue/55"><span className="px-2 py-0.5 bg-brand-orange/10 text-brand-orange font-bold rounded">{p.category}</span><span className="flex items-center gap-1"><Calendar size={11}/>{p.date}</span></div>
                <h3 className="font-poppins font-bold text-brand-blue text-[17px] mt-3 leading-snug group-hover:text-brand-orange transition-colors line-clamp-2">{p.title}</h3>
                <p className="text-brand-blue/65 text-[13.5px] mt-2 line-clamp-2 leading-relaxed">{p.excerpt}</p>
                <div className="mt-3 pt-3 border-t border-brand-grey flex items-center justify-between"><span className="text-[12px] text-brand-blue/60 flex items-center gap-1.5"><User size={12}/>{p.author}</span><span className="text-brand-orange font-semibold text-[12.5px] flex items-center gap-1">Read <ArrowRight size={12}/></span></div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <EnquiryForm/>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug) || BLOG_POSTS[0];
  return (
    <div>
      <Helmet>
        <title>{post.title} | Mountains Chain Travel Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={`${post.title} | Mountains Chain Travel Blog`} />
        <meta property="og:description" content={post.excerpt} />
      </Helmet>
      <PageHero title={post.title} sub={`By ${post.author} · ${post.date} · ${post.category}`} image={post.image}/>
      <CustomerVideoReviews />
      <article className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-brand-blue/80 text-[16px] leading-[1.75] font-medium">{post.excerpt}</p>
        {post.content.map((para,i) => (<p key={i} className="text-brand-blue/75 text-[15.5px] leading-[1.8] mt-5">{para}</p>))}
        <div className="mt-10 p-6 bg-brand-grey rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div><div className="font-poppins font-bold text-brand-blue text-[18px]">Ready to plan your trip?</div><div className="text-brand-blue/60 text-[13px]">Talk to our travel designer - free, no obligation.</div></div>
          <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI read the blog "' + post.title + '" and I\'m inspired to plan a similar trip.\n\nCould you help me design an itinerary?\n\nThank you!')}`} target="_blank" rel="noreferrer"><Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-11 px-5 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>WhatsApp Us</Button></a>
        </div>
      </article>
      <EnquiryForm/>
    </div>
  );
}

export function DestinationWeddingPage() {
  const whatsappLink = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nWe are interested in planning a destination wedding in Himachal Pradesh.\n\nPlease share:\n- Available Himachal wedding venues\n- Wedding packages & pricing\n- Customization details\n\nLooking forward to hearing from you!')}`;

  const himachalVenues = [
    { name: 'Shimla', tagline: 'The Queen of Hills', img: '/images/Assets/Images/new/shimla.webp', tag: 'Most Popular', highlights: ['Heritage Colonial Venues', 'Panoramic Valley Views', 'Perfect Year-Round Climate'] },
    { name: 'Dharamshala', tagline: 'Where Spirituality Meets Romance', img: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?crop=entropy&cs=srgb&fm=jpg&q=85', highlights: ['Dhauladhar Mountain Backdrop', 'Luxury Resort Venues', 'Serene Forest Settings'] },
    { name: 'Manali', tagline: 'Snow-Capped Romance', img: 'https://images.unsplash.com/photo-1571401835393-8c5f35328320?crop=entropy&cs=srgb&fm=jpg&q=85', highlights: ['Riverside Ceremony Venues', 'Himalayan Peak Views', 'Adventure + Wedding Combo'] },
    { name: 'Kasauli', tagline: 'Hidden Gem Weddings', img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=srgb&fm=jpg&q=85', highlights: ['Intimate Boutique Venues', 'Colonial Architecture', 'Private Estate Weddings'] },
    { name: 'Dalhousie', tagline: 'The Scottish Hills of India', img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=srgb&fm=jpg&q=85', highlights: ['Grand Estate Venues', 'Misty Valley Settings', 'Old-World Charm'] },
    { name: 'Chail & Palampur', tagline: 'Off-Beat Luxury', img: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?crop=entropy&cs=srgb&fm=jpg&q=85', highlights: ['Tea Garden Ceremonies', 'Palace & Fort Venues', 'Unique & Exclusive'] },
  ];

  const whyChooseUs = [
    { icon: '🏛️', title: 'Govt. of HP Recognized & Certified', desc: 'Only HP-certified destination wedding specialist in Himachal Pradesh.' },
    { icon: '🏔️', title: 'Exclusive Himalayan Venues', desc: 'Access to 50+ premium mountain venues across Himachal.' },
    { icon: '💐', title: 'End-to-End Wedding Management', desc: 'Decor to catering, we do it all - one team, one contract.' },
    { icon: '💰', title: 'Packages from ₹5 Lakh', desc: 'Transparent pricing, no hidden costs. Fully customizable.' },
    { icon: '📷', title: 'Photography & Decor Included', desc: 'In-house creative team for cinematic captures.' },
    { icon: '👰', title: 'Dedicated Wedding Coordinator', desc: 'Your personal planner available 24/7 throughout.' },
  ];

  const packages = [
    {
      name: 'Silver',
      price: '₹5 Lakh',
      guests: '50–100 Guests',
      desc: 'A beautiful intimate ceremony in the mountains.',
      includes: ['1 Wedding Venue', 'Basic Decor & Florals', 'Catering (Veg Menu)', '1 Photographer', '1 Day Event Management', 'Dedicated Coordinator'],
    },
    {
      name: 'Gold',
      price: '₹12 Lakh',
      guests: '100–250 Guests',
      desc: 'Our most booked tier - full weekend celebration.',
      featured: true,
      includes: ['Premium Venue Selection', 'Designer Decor & Florals', 'Multi-Cuisine Catering', 'Photo + Video Team', '2-Day Event (Sangeet + Wedding)', 'Guest Accommodation Assist', 'Dedicated Team of 3'],
    },
    {
      name: 'Diamond',
      price: '₹25 Lakh',
      guests: '250–500+ Guests',
      desc: 'A palace-scale Himalayan wedding with every luxury.',
      includes: ['Luxury Venue + Backup', 'Bespoke Luxury Decor', 'Celebrity Chef Catering', 'Full Photo + Drone + Film', '3-Day Events (Full Wedding)', 'Complete Guest Management', 'Travel & Honeymoon Included'],
    },
  ];

  const steps = [
    { n: '1', t: 'Share Your Vision', d: 'Tell us your dream - Himachal destination, budget, guest count, must-haves.' },
    { n: '2', t: 'Get Custom Proposal', d: 'Receive a tailored wedding plan within 24 hours with venues and pricing.' },
    { n: '3', t: 'Finalize & Book', d: 'Lock your venue, sign the contract, and pay just 20% to confirm dates.' },
    { n: '4', t: 'Celebrate Your Day', d: 'Sit back and enjoy - we handle every single detail from start to finish.' },
  ];

  const faqs = [
    { q: 'What is the best time for a destination wedding in Himachal Pradesh?', a: 'October to February is ideal for a snow-themed wedding in Manali or Shimla. March to June is perfect for Dharamshala, Kasauli, and Dalhousie with pleasant temperatures. Monsoon (July–September) can be magical in Palampur and Chail but we advise backup venues.' },
    { q: 'How far in advance should I book?', a: 'We recommend booking at least 6–12 months in advance for 2025 and 2026 dates, especially for peak season (October–February). Popular venues fill up quickly. We hold tentative dates for 7 days after your inquiry at no cost.' },
    { q: 'Do you handle guest travel and accommodation?', a: 'Yes! We coordinate everything - airport/station pickups, hotel blocks at negotiated rates, and transportation throughout the wedding. Our Gold and Diamond packages include full guest management services.' },
    { q: 'Which Himachal city is best for a destination wedding?', a: 'Shimla suits couples wanting colonial charm and year-round accessibility. Manali is perfect for adventure-loving couples wanting dramatic snow peaks. Dharamshala offers serene mountain spirituality. Kasauli and Dalhousie are ideal for intimate, boutique weddings.' },
    { q: 'Is Mountains Chain Travel government recognized?', a: 'Yes! We are officially recognized by the Government of Himachal Pradesh and registered with HP Tourism. This makes us the only certified destination wedding specialist in Himachal Pradesh, ensuring complete legal and logistical compliance.' },
    { q: 'What is the minimum budget for a destination wedding in Himachal?', a: 'Our packages start from ₹5 Lakh for intimate weddings of 50–100 guests. We customize every package to your vision and budget, so please share your requirements and we will create a plan that fits perfectly.' },
  ];

  return (
    <div>
      <Helmet>
        <title>Destination Wedding Planner in Himachal Pradesh | Mountains Chain Travel</title>
        <meta name="description" content="Plan your dream destination wedding in Shimla, Manali, Dharamshala, Kasauli, Dalhousie & Chail. Govt. of HP recognized. 500+ weddings planned. Packages from ₹5 Lakh." />
        <meta property="og:title" content="Destination Wedding Planner in Himachal Pradesh | Mountains Chain Travel" />
        <meta property="og:description" content="Plan your dream destination wedding in Himachal Pradesh. Shimla, Manali, Dharamshala, Kasauli, Dalhousie & Chail. End-to-end planning from ₹5 Lakh." />
      </Helmet>

      {/* Trust Strip */}
      <div className="bg-brand-blue text-white py-3 overflow-hidden">
        <div className="flex items-center justify-center gap-8 text-[12px] font-semibold whitespace-nowrap animate-[scroll_20s_linear_infinite]">
          {['🏆 Govt. of Himachal Recognized', '⭐ 500+ Weddings Planned', '🌟 4.9/5 Google Rating', '📅 10+ Years Experience', '💯 100% Customized Packages', '📍 7+ Wedding Destinations'].map((item, i) => (
            <span key={i} className="flex items-center gap-2">{item}</span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Himachal Pradesh Destination Wedding" className="absolute inset-0 w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/65 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/80 via-transparent to-brand-blue/30 z-10" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40 animate-fade-up">
              <span className="tracking-wide">Govt. of Himachal Recognized · 500+ Weddings Planned</span>
            </div>
            <div className="text-white/90 text-[14px] lg:text-[15px] font-semibold mb-3 animate-fade-up" style={{animationDelay: '50ms'}}>
              Shimla · Dharamshala · Manali · Kasauli · Dalhousie · Chail
            </div>
            <h1 className="font-poppins text-[44px] sm:text-[56px] lg:text-[76px] text-white leading-[1.02] font-extrabold drop-shadow-2xl animate-fade-up" style={{animationDelay: '100ms'}}>
              Your Dream Destination Wedding in{' '}
              <span className="text-brand-orange">Himachal Pradesh</span>
            </h1>
            <p className="text-white text-[17px] lg:text-[20px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg animate-fade-up" style={{animationDelay: '200ms'}}>
              Plan the most magical day of your life amidst the snow-capped Himalayas. From grand venues to every tiny detail - we handle it all.
            </p>
            <div className="flex flex-wrap gap-4 mt-10 animate-fade-up" style={{animationDelay: '300ms'}}>
              <a href={whatsappLink} target="_blank" rel="noreferrer" className="group">
                <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-13 px-8 rounded-xl text-[16px] font-bold btn-glow shadow-2xl shadow-brand-orange/40 transition-all duration-300 group-hover:scale-105">
                  <MessageCircle size={20} className="mr-2.5"/>
                  Plan My Wedding
                </Button>
              </a>
              <a href="#wd-packages" className="group">
                <Button variant="outline" className="border-2 border-white/50 text-white hover:bg-white/20 h-13 px-8 rounded-xl text-[16px] font-semibold transition-all duration-300 group-hover:scale-105">
                  View Packages <ArrowRight size={18} className="ml-2"/>
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap gap-8 mt-12 animate-fade-up" style={{animationDelay: '400ms'}}>
              {[
                { num: '500+', label: 'Weddings Planned' },
                { num: '7+', label: 'Himachal Destinations' },
                { num: '4.9', label: '★ Couple Rating' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-brand-orange font-poppins font-extrabold text-[28px] lg:text-[32px] leading-none">{stat.num}</div>
                  <div className="text-white/80 text-[12px] font-medium mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Review Cards - Right after hero */}
      <ReviewCards />

      {/* Wedding Destinations */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="text-center mb-14">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">Wedding Destinations</div>
          <h2 className="font-poppins text-[32px] lg:text-[44px] text-brand-blue leading-[1.1]">Choose Your Dream <span className="text-brand-orange">Wedding Destination</span></h2>
          <p className="text-brand-blue/65 text-[15px] max-w-2xl mx-auto mt-3 leading-relaxed">From snow-capped Shimla to spiritual Dharamshala - every corner of Himachal is made for love</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {himachalVenues.map((v, i) => (
            <div key={v.name} className="group bg-white rounded-2xl overflow-hidden border border-brand-greyDark shadow-card card-hover animate-fade-up" style={{animationDelay: `${i*80}ms`}}>
              <div className="relative h-56 overflow-hidden">
                <img src={v.img} alt={v.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/80 via-brand-blue/20 to-transparent" />
                {v.tag && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-brand-orange text-white text-[10.5px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">{v.tag}</div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-poppins font-extrabold text-white text-[24px] leading-tight">{v.name}</h3>
                  <p className="text-white/85 text-[13px] mt-1">{v.tagline}</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-2">
                  {v.highlights.map((h, j) => (
                    <li key={j} className="flex items-center gap-2 text-brand-blue/75 text-[13.5px]">
                      <CheckCircle2 size={14} className="text-brand-orange shrink-0"/>{h}
                    </li>
                  ))}
                </ul>
                <a href={whatsappLink} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-brand-orange font-semibold text-[13px] hover:gap-2.5 transition-all">
                  Explore Venues <ArrowRight size={13}/>
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-brand-grey py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">Why Choose Us</div>
            <h2 className="font-poppins text-[32px] lg:text-[44px] text-brand-blue leading-[1.1]">Why Himachal Trusts <span className="text-brand-orange">Mountains Chain</span> for Destination Weddings</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChooseUs.map((f, i) => (
              <div key={i} className="bg-white border border-brand-greyDark rounded-2xl p-6 card-hover animate-fade-up" style={{animationDelay: `${i*70}ms`}}>
                <div className="text-[40px] mb-3">{f.icon}</div>
                <h3 className="font-poppins font-bold text-brand-blue text-[17px]">{f.title}</h3>
                <p className="text-brand-blue/65 text-[14px] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="text-center mb-14">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">Simple Process</div>
          <h2 className="font-poppins text-[32px] lg:text-[44px] text-brand-blue leading-[1.1]">How It Works</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={i} className="relative bg-white border border-brand-greyDark rounded-2xl p-6 card-hover animate-fade-up" style={{animationDelay: `${i*80}ms`}}>
              <div className="absolute -top-4 left-6 w-11 h-11 rounded-xl bg-brand-orange text-white font-poppins font-bold flex items-center justify-center shadow-glow-orange text-[18px]">{s.n}</div>
              <h3 className="font-poppins font-bold text-brand-blue text-[17px] mt-3">{s.t}</h3>
              <p className="text-brand-blue/65 text-[13.5px] mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section id="wd-packages" className="bg-brand-grey py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-3">Transparent Pricing</div>
            <h2 className="font-poppins text-[32px] lg:text-[44px] text-brand-blue leading-[1.1]">Wedding Packages - <span className="text-brand-orange">No Hidden Costs</span></h2>
            <p className="text-brand-blue/65 text-[15px] max-w-2xl mx-auto mt-3 leading-relaxed">Choose a package or let us build a custom one for you. Every package is fully customizable.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((p, i) => (
              <div key={p.name} className={`relative rounded-2xl p-7 card-hover animate-fade-up ${p.featured ? 'bg-brand-blue text-white border-2 border-brand-orange shadow-premium md:-translate-y-2' : 'bg-white border border-brand-greyDark shadow-card'}`} style={{animationDelay: `${i*80}ms`}}>
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-orange text-white text-[10.5px] font-bold px-3 py-1 rounded-full uppercase tracking-wider whitespace-nowrap">Most Popular</div>
                )}
                <div className="text-brand-orange text-[12px] font-bold uppercase tracking-wider mb-1">{p.name}</div>
                <div className={`font-poppins font-extrabold text-[40px] leading-none ${p.featured ? 'text-white' : 'text-brand-blue'}`}>{p.price}</div>
                <div className={`text-[13px] mt-1 ${p.featured ? 'text-white/70' : 'text-brand-blue/55'}`}>{p.guests}</div>
                <p className={`text-[13.5px] mt-3 leading-relaxed ${p.featured ? 'text-white/80' : 'text-brand-blue/65'}`}>{p.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {p.includes.map((x, j) => (
                    <li key={j} className={`flex items-start gap-2 text-[13.5px] ${p.featured ? 'text-white/85' : 'text-brand-blue/80'}`}>
                      <CheckCircle2 size={16} className={`mt-0.5 shrink-0 ${p.featured ? 'text-brand-orange' : 'text-emerald-600'}`}/>{x}
                    </li>
                  ))}
                </ul>
                <a href={whatsappLink} target="_blank" rel="noreferrer" className="block mt-6">
                  <Button className={`w-full h-11 rounded-xl font-semibold ${p.featured ? 'bg-brand-orange hover:bg-brand-orangeDark text-white btn-glow' : 'bg-brand-blue hover:bg-brand-blueDark text-white'}`}>
                    <MessageCircle size={16} className="mr-2"/>Get {p.name} Package
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Frequently Asked Questions</div>
          <h2 className="font-poppins text-[30px] lg:text-[40px] text-brand-blue leading-[1.1]">Wedding planning, <span className="text-brand-orange">answered</span></h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`dw-faq-${i}`} className="border-brand-greyDark">
              <AccordionTrigger className="text-left text-brand-blue font-semibold text-[15px] lg:text-[16px] hover:text-brand-orange hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-brand-blue/70 text-[14.5px] leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA Banner */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue to-brand-blue/95" />
        <img src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-poppins text-[32px] lg:text-[44px] text-white leading-[1.15]">Ready to Say 'I Do' in the Mountains?</h2>
          <p className="text-white/75 text-[16px] mt-3 max-w-xl mx-auto leading-relaxed">Your dream Himachal wedding is one conversation away. Let our experts create a custom plan just for you.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a href={whatsappLink} target="_blank" rel="noreferrer">
              <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-7 rounded-xl text-[15px] font-bold btn-glow shadow-xl shadow-brand-orange/30"><MessageCircle size={18} className="mr-2"/>Plan on WhatsApp</Button>
            </a>
            <a href={`tel:${COMPANY.phone}`}>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-7 rounded-xl text-[15px]"><Phone size={18} className="mr-2"/>{COMPANY.phone}</Button>
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-6 text-[13px] text-white/70">
            <span>✅ Govt Recognized</span>
            <span>⭐ 500+ Weddings</span>
            <span>💕 Free Consultation</span>
          </div>
        </div>
      </section>

      <div id="dw-enquire">
        <EnquiryForm />
      </div>
    </div>
  );
}
